import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  YMap,
  YMapCollection,
  YMapComponentsProvider,
  YMapControlButton,
  YMapControls,
  YMapDefaultFeaturesLayer,
  YMapDefaultSchemeLayer,
  YMapGeolocationControl,
  YMapListener,
  YMapMarker,
  YMapZoomControl
} from 'ymap3-components'
import { fetchDeviceUtilizationSeries } from '../../api/dashboard'
import { DEFAULT_MAP_LOCATION } from '../../lib/constants/cities'
import { getTimeRangeParams, LOGS_TIME_RANGES } from '../../utils/timeRanges'
import { SchoolActionButtons } from '../buttons'
import HistoryGraphModal from '../HistoryGraphModal'
import SpeedtestModalWebSocket from '../SpeedtestModalWebSocket'
import { StatusDot } from './schoolEquipmentChrome'
import { Chip } from '../ui/surface'
import styles from './MapSection.module.css'

const getStatusMeta = (t) => ({
  up: { label: t('common.online'), color: '#2AD100', markerClass: styles.markerUp },
  degraded: { label: t('map.degraded'), color: '#FFAD17', markerClass: styles.markerDegraded },
  down: { label: t('common.offline'), color: '#FF4D4F', markerClass: styles.markerDown },
});

/** Parse valid #hex label color; returns null if invalid. */
const parseValidHex = (color) => {
  if (!color || typeof color !== 'string') return null;
  const hex = color.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return hex;
};

/** Glow/pulse styles for label-colored layers; core fill stays status color from CSS. Badge shows label color. */
const markerLabelLayerStyles = (status, labelColor) => {
  if (!labelColor || typeof labelColor !== 'string') return null;
  const hex = labelColor.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;

  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const rgba = (a) => `rgba(${r},${g},${b},${a})`;

  if (status === 'down') {
    return {
      glow: {
        background: `radial-gradient(circle, ${rgba(0.14)}, ${rgba(0)})`,
      },
      pulse: {
        backgroundColor: rgba(0.24),
      },
    };
  }

  if (status === 'up') {
    return {
      glow: {
        background: 'radial-gradient(circle, rgba(42, 209, 0, 0.32), rgba(42, 209, 0, 0))',
      },
      pulse: {
        backgroundColor: 'rgba(42, 209, 0, 0.28)',
      },
    };
  }

  // degraded
  return {
    glow: {
      background: 'radial-gradient(circle, rgba(255, 173, 23, 0.28), rgba(255, 173, 23, 0))',
    },
    pulse: {
      backgroundColor: 'rgba(255, 173, 23, 0.28)',
    },
  };
};

const DEFAULT_KEY = process.env.REACT_APP_YMAP_API_KEY;

const normalizeMarkers = (markers) => {
  if (!Array.isArray(markers)) {
    return [];
  }

  return markers
    .map((marker) => {
      const lat = Number(marker?.lat ?? marker?.latitude);
      const lon = Number(marker?.lon ?? marker?.longitude ?? marker?.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
      }

      return {
        ...marker,
        lat,
        lon,
        status: marker?.status?.toLowerCase?.() || 'down',
      };
    })
    .filter(Boolean);
};

const getLocationFromMarkers = (markers) => {
  return DEFAULT_MAP_LOCATION;
  if (!markers.length) {
    return DEFAULT_MAP_LOCATION;
  }

  const avgLon =
    markers.reduce((sum, marker) => sum + Number(marker.lon), 0) / markers.length;
  const avgLat =
    markers.reduce((sum, marker) => sum + Number(marker.lat), 0) / markers.length;

  const lats = markers.map((marker) => Number(marker.lat));
  const lons = markers.map((marker) => Number(marker.lon));

  const latRange = Math.max(...lats) - Math.min(...lats);
  const lonRange = Math.max(...lons) - Math.min(...lons);
  const maxRange = Math.max(latRange, lonRange);

  const zoom = (() => {
    if (markers.length === 1) return 10;
    if (maxRange < 0.5) return 11;
    if (maxRange < 1.5) return 9;
    if (maxRange < 3) return 7.5;
    if (maxRange < 8) return 6.2;
    return 5;
  })();

  return {
    center: [avgLon, avgLat],
    zoom,
  };
};

const MapFallback = ({ reason }) => (
  <div className={styles.mapFallback}>
    {reason}
  </div>
);

const MapSidebar = ({ markerData, onClose, isOpen, onOpenHistory, onOpenSpeedtest, showActions = true, t, statusMeta }) => {
  const navigate = useNavigate();

  if (!markerData) {
    return null;
  }

  const {
    name,
    status,
    ip,
    lat,
    lon,
    phone,
    address,
    photo_url,
    region,
    additional_url,
    label_name: labelName,
    label_color: labelColor,
  } = markerData;
  const statusInfo = statusMeta[status] || statusMeta.down;

  const handleGoToSchool = () => {
    if (ip) {
      navigate(`/schools/${encodeURIComponent(ip)}`);
    }
  };

  return (
    <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
      <div className={styles.sidebarContent}>
        <button
          onClick={onClose}
          className={styles.sidebarCloseButton}
          type="button"
        >
          <span className="material-icons text-lg">close</span>
        </button>
        
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarHeaderRow}>
            <h3 className={styles.sidebarTitle}>{name}</h3>
            <span
              className={styles.sidebarStatus}
              style={{ color: statusInfo.color }}
            >
              <span className="material-icons text-sm">lens</span>
              {statusInfo.label}
            </span>
          </div>
          <div className={styles.sidebarMetaRow}>
            {region && <span className={styles.sidebarMetaItem}>{region}</span>}
            {ip && <span className={styles.sidebarMetaItem}>{ip}</span>}
            <span className={styles.sidebarMetaItem}>
              {lat.toFixed(3)}°, {lon.toFixed(3)}°
            </span>
          </div>
        </div>

        {/* Фото школы - увеличенное */}
        {photo_url ? (
          <div className={styles.sidebarPhoto}>
            <img 
              src={photo_url} 
              alt={name}
              className={styles.sidebarPhotoImg}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className={styles.sidebarPhotoPlaceholder} style={{ display: 'none' }}>
              <span className="material-icons">school</span>
            </div>
          </div>
        ) : (
          <div className={styles.sidebarPhoto}>
            <div className={styles.sidebarPhotoPlaceholder}>
              <span className="material-icons">school</span>
            </div>
          </div>
        )}
        
        <div className={styles.sidebarBody}>
          <div className={styles.sidebarInfo}>
            {/* Адрес */}
            {address ? (
              <div className={styles.sidebarInfoItem}>
                <span className={styles.sidebarInfoLabel}>{t('map.address')}:</span>
                <span className={styles.sidebarInfoValue}>{address}</span>
              </div>
            ) : (
              <div className={styles.sidebarInfoItem}>
                <span className={styles.sidebarInfoLabel}>{t('map.address')}:</span>
                <span className={styles.sidebarInfoValuePlaceholder}>{t('map.addressNotSpecified')}</span>
              </div>
            )}

            {/* Телефон */}
            {phone ? (
              <div className={styles.sidebarInfoItem}>
                <span className={styles.sidebarInfoLabel}>{t('map.phone')}:</span>
                <span className={styles.sidebarInfoValue}>
                  <a href={`tel:${phone.replace(/\s/g, '')}`} className={styles.sidebarPhoneLink}>
                    {phone}
                  </a>
                </span>
              </div>
            ) : (
              <div className={styles.sidebarInfoItem}>
                <span className={styles.sidebarInfoLabel}>{t('map.phone')}:</span>
                <span className={styles.sidebarInfoValuePlaceholder}>{t('map.phoneNotSpecified')}</span>
              </div>
            )}

            {labelName ? (
              <div className={styles.sidebarInfoItem}>
                <span className={styles.sidebarInfoLabel}>{t('devices.label')}:</span>
                <span
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: labelColor || '#e2e8f0' }}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: labelColor || '#94a3b8' }}
                  />
                  {labelName}
                </span>
              </div>
            ) : null}
          </div>
          
          {ip && showActions && (
            <SchoolActionButtons
              schoolIp={ip}
              schoolName={name}
              onOpenHistory={onOpenHistory}
              onOpenSpeedtest={onOpenSpeedtest}
              additionalUrl={additional_url}
            />
          )}
          
          <button
            onClick={handleGoToSchool}
            className={styles.sidebarButton}
            type="button"
          >
            <span className="material-icons text-sm">arrow_forward</span>
            {t('map.goToSchool')}
          </button>
        </div>
      </div>
    </div>
  );
};

const MapSection = ({ markers, className, disableDefaultLayout = false, title, subtitle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiKey = DEFAULT_KEY;
  const normalizedMarkers = useMemo(() => normalizeMarkers(markers), [markers]);
  const STATUS_META = getStatusMeta(t);

  // Всегда начинаем с дефолтной локации (Шымкентская область)
  const [mapLocation, setMapLocation] = useState(DEFAULT_MAP_LOCATION);

  const [userInteracted, setUserInteracted] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  // 👇 добавляем hover hint (как в примере)
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const mapWrapperRef = useRef(null);
useEffect(() => {
  const wrapper = mapWrapperRef.current;
  if (!wrapper) return;

  const handleMouseMove = (event) => {
    const rect = wrapper.getBoundingClientRect();
    setMousePos({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  wrapper.addEventListener('mousemove', handleMouseMove);
  return () => wrapper.removeEventListener('mousemove', handleMouseMove);
}, []);

  const displayedMarkers = useMemo(() => {
    return normalizedMarkers.filter((marker) => {
      if (selectedRegion && marker.region !== selectedRegion) {
        return false;
      }
      if (schoolSearch.trim() && !marker.name?.toLowerCase().includes(schoolSearch.toLowerCase())) {
        return false;
      }
      if (selectedLabel && marker.label_name !== selectedLabel) {
        return false;
      }
      return true;
    });
  }, [normalizedMarkers, selectedRegion, schoolSearch, selectedLabel]);

  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyTimeRange, setHistoryTimeRange] = useState('24h');
  const [historyChartData, setHistoryChartData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentDeviceIp, setCurrentDeviceIp] = useState(null);
  const [speedtestModalOpen, setSpeedtestModalOpen] = useState(false);

  const markersSignature = useMemo(
    () =>
      normalizedMarkers
        .map(
          (marker) =>
            `${marker.lat}:${marker.lon}:${marker.status}:${marker.label_color || ''}`,
        )
        .join('|'),
    [normalizedMarkers]
  );

  const stabilizedMarkers = useRef(markersSignature);

  useEffect(() => {
    if (stabilizedMarkers.current === markersSignature) {
      return;
    }

    stabilizedMarkers.current = markersSignature;

    if (userInteracted) {
      return;
    }

    // Если есть маркеры, вычисляем локацию, иначе используем дефолтную
    if (normalizedMarkers && normalizedMarkers.length > 0) {
      setMapLocation(getLocationFromMarkers(normalizedMarkers));
    } else {
      setMapLocation(DEFAULT_MAP_LOCATION);
    }
  }, [markersSignature, normalizedMarkers, userInteracted]);

  const availableRegions = useMemo(() => {
    const regionsSet = new Set();
    normalizedMarkers.forEach((marker) => {
      if (marker.region) {
        regionsSet.add(marker.region);
      }
    });
    return Array.from(regionsSet).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [normalizedMarkers]);

  const availableLabels = useMemo(() => {
    const map = new Map();
    normalizedMarkers.forEach((marker) => {
      if (marker.label_name && !map.has(marker.label_name)) {
        map.set(marker.label_name, marker.label_color || '#94a3b8');
      }
    });
    return Array.from(map.entries())
      .map(([name, color]) => ({ name, color }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [normalizedMarkers]);

  const availableSchools = useMemo(() => {
    const schoolsSet = new Set();
    normalizedMarkers.forEach(marker => {
      if (selectedRegion && marker.region !== selectedRegion) {
        return;
      }
      if (marker.name) {
        schoolsSet.add(marker.name);
      }
    });
    return Array.from(schoolsSet).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [normalizedMarkers, selectedRegion]);

  const filteredSchools = useMemo(() => {
    if (!schoolSearch.trim()) {
      return availableSchools;
    }
    return availableSchools.filter(school =>
      school.toLowerCase().includes(schoolSearch.toLowerCase())
    );
  }, [availableSchools, schoolSearch]);

  const statusSummary = useMemo(() => {
    return displayedMarkers.reduce(
      (acc, marker) => {
        const status = marker.status;
        const key = STATUS_META[status] ? status : 'down';
        acc[key] += 1;
        return acc;
      },
      { up: 0, degraded: 0, down: 0 }
    );
  }, [displayedMarkers]);

  const handleMapUpdate = useCallback(({ location, mapInAction }) => {
    if (mapInAction) {
      setUserInteracted(true);
      setMapLocation({ center: location.center, zoom: location.zoom });
    }
  }, []);

  const handleMarkerClick = useCallback((marker, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setSelectedMarker(marker);
    setSidebarOpen(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
    setSelectedMarker(null);
  }, []);

  const handleRegionSelect = useCallback((region) => {
    setSelectedRegion(region);
    setSchoolSearch('');
    
    const regionMarkers = normalizedMarkers.filter(m => m.region === region);
    if (regionMarkers.length > 0) {
      const location = getLocationFromMarkers(regionMarkers);
      setMapLocation(location);
      setUserInteracted(true);
    }
  }, [normalizedMarkers]);

  const handleClearRegion = useCallback(() => {
    setSelectedRegion('');
    setSchoolSearch('');
    // Если есть маркеры, используем их локацию, иначе дефолтную
    if (normalizedMarkers && normalizedMarkers.length > 0) {
      const location = getLocationFromMarkers(normalizedMarkers);
      setMapLocation(location);
    } else {
      setMapLocation(DEFAULT_MAP_LOCATION);
    }
    setUserInteracted(true);
  }, [normalizedMarkers]);

  const handleLabelSelect = useCallback((labelName) => {
    setSelectedLabel((prev) => (prev === labelName ? '' : labelName));
  }, []);

  const handleClearLabel = useCallback(() => {
    setSelectedLabel('');
  }, []);

  const handleSchoolSelect = useCallback((schoolName) => {
    setSchoolSearch('');
    
    const schoolMarker = normalizedMarkers.find(m => m.name === schoolName);
    if (schoolMarker) {
      setMapLocation({ 
        center: [schoolMarker.lon, schoolMarker.lat], 
        zoom: 13 
      });
      setUserInteracted(true);
      
      setSelectedMarker(schoolMarker);
      setSidebarOpen(true);
    }
  }, [normalizedMarkers]);
  
  const buildDeviceChartData = useCallback((series, t) => {
    if (!series?.in?.length || !series?.out?.length) {
      return null;
    }

    return {
      labels: series.in.map((point) =>
        new Date(point[0] * 1000).toLocaleString('ru-KZ', {
          timeZone: 'Asia/Almaty',
          hour: '2-digit',
          minute: '2-digit',
        }),
      ),
      rawTimestamps: series.in.map((point) => point[0]),
      datasets: [
        {
          label: t('dashboard.incomingTraffic'),
          data: series.in.map((point) => parseFloat(point[1])),
        },
        {
          label: t('dashboard.outgoingTraffic'),
          data: series.out.map((point) => parseFloat(point[1])),
        },
      ],
    };
  }, []);

  const handleOpenHistory = useCallback(async (deviceIp) => {
    setCurrentDeviceIp(deviceIp);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    
    try {
      const params = getTimeRangeParams(historyTimeRange, LOGS_TIME_RANGES);
      const series = await fetchDeviceUtilizationSeries(deviceIp, params);
      setHistoryChartData(buildDeviceChartData(series, t));
    } catch (error) {
      setHistoryChartData(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyTimeRange, buildDeviceChartData, t]);
  
  const handleHistoryTimeRangeChange = useCallback(async (newRange) => {
    setHistoryTimeRange(newRange);
    if (!currentDeviceIp) return;
    
    setHistoryLoading(true);
    try {
      const params = getTimeRangeParams(newRange, LOGS_TIME_RANGES);
      const series = await fetchDeviceUtilizationSeries(currentDeviceIp, params);
      setHistoryChartData(buildDeviceChartData(series, t));
    } catch (error) {
      setHistoryChartData(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [currentDeviceIp, buildDeviceChartData, t]);

  const handleOpenSpeedtest = useCallback((deviceIp) => {
    setCurrentDeviceIp(deviceIp);
    setSpeedtestModalOpen(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && sidebarOpen) {
        handleCloseSidebar();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, handleCloseSidebar]);

  const canRenderMap = Boolean(apiKey) && Boolean(displayedMarkers.length);

  const summaryItems = [
    { key: 'up', label: t('common.online'), value: statusSummary?.up ?? 0 },
    { key: 'down', label: t('common.offline'), value: statusSummary?.down ?? 0 },
  ];

  const baseSectionClasses =
    'relative flex flex-col overflow-hidden rounded-3xl bg-surface-1';

  const defaultLayoutClasses = 'col-span-12 xl:col-span-12 2xl:col-span-12';
  const sectionClasses = [
    baseSectionClasses,
    disableDefaultLayout ? '' : defaultLayoutClasses,
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const heading = title || t('map.title');
  const caption = subtitle === undefined ? t('map.subtitle') : subtitle;

  return (
    <section className={sectionClasses}>
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
      <header
        className={
          disableDefaultLayout
            ? 'flex min-h-9 items-center justify-between gap-3'
            : 'flex flex-col justify-between gap-3 sm:flex-row sm:items-center'
        }
      >
        <div className={disableDefaultLayout ? 'flex min-w-0 items-end gap-3' : undefined}>
          <h3 className={`font-display text-lg text-white ${disableDefaultLayout ? 'shrink-0 leading-none' : ''}`}>{heading}</h3>
          {caption ? (
            <p className={`text-xs text-muted-400 ${disableDefaultLayout ? 'truncate pb-px leading-none' : 'mt-1'}`}>{caption}</p>
          ) : null}
        </div>
        {markers?.length !== 1 && (
        <div className="flex flex-wrap items-center gap-3">
          {summaryItems.map(({ key, label, value }) => (
            <StatusDot
              key={key}
              size="lg"
              tone={key === 'up' ? 'ok' : 'critical'}
              label={`${label}: ${value}`}
            />
          ))}
        </div>
        )}
      </header>

      <div className={styles.mapStage}>
      {!disableDefaultLayout && (
        <div className="space-y-3 px-2 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
              {t('map.regions')}:
            </span>
            
            {selectedRegion && (
              <Chip icon="close" onClick={handleClearRegion}>
                {t('map.reset')}
              </Chip>
            )}

            {availableRegions.map((region) => (
              <Chip
                key={region}
                icon="location_city"
                active={selectedRegion === region}
                onClick={() => handleRegionSelect(region)}
              >
                {region}
              </Chip>
            ))}
          </div>

          {availableLabels.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                {t('map.labels')}:
              </span>

              {selectedLabel && (
                <Chip icon="close" onClick={handleClearLabel}>
                  {t('map.reset')}
                </Chip>
              )}

              {availableLabels.map(({ name, color }) => (
                <Chip
                  key={name}
                  active={selectedLabel === name}
                  onClick={() => handleLabelSelect(name)}
                  style={selectedLabel === name ? { backgroundColor: `${color}22`, color } : undefined}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                  {name}
                </Chip>
              ))}
            </div>
          )}

          {availableSchools.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                {t('map.schools')}:
              </span>
              
              <input
                type="text"
                placeholder={t('map.searchSchool')}
                value={schoolSearch}
                onChange={(e) => setSchoolSearch(e.target.value)}
                className="w-48 rounded-full bg-white/[0.04] px-3 py-1.5 text-xs text-white outline-none placeholder:text-muted-500 focus-visible:ring-2 focus-visible:ring-brand-400"
              />
              
              {schoolSearch.trim() && filteredSchools.length > 0 && (
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {filteredSchools.slice(0, 10).map((school) => (
                    <Chip key={school} icon="school" onClick={() => handleSchoolSelect(school)}>
                      {school}
                    </Chip>
                  ))}
                  {filteredSchools.length > 10 && (
                    <span className="text-xs text-muted-400 italic px-2">
                      {t('map.andMore', { count: filteredSchools.length - 10 })}
                    </span>
                  )}
                </div>
              )}
              
              {schoolSearch.trim() && filteredSchools.length === 0 && (
                <span className="text-xs text-muted-400 italic">{t('map.schoolNotFound')}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div ref={mapWrapperRef} className={`${styles.mapWrapper} relative ${disableDefaultLayout ? 'h-full' : 'min-h-[720px]'}`}>
        {!apiKey && (
          <MapFallback reason={t('map.addYandexKey')} />
        )}
        {apiKey && !displayedMarkers.length && normalizedMarkers.length === 0 && (
          <MapFallback reason={t('map.noDataForMap')} />
        )}
        {apiKey && displayedMarkers.length === 0 && normalizedMarkers.length > 0 && (
          <MapFallback reason={t('map.noObjectsForFilters')} />
        )}
        {canRenderMap && (
          <YMapComponentsProvider apiKey={apiKey} lang="ru_RU">
            <YMap
              key="dashboard-map"
              location={mapLocation}
              mode="vector"
              theme="dark"
              className={styles.mapCanvas}
            >
              <YMapDefaultSchemeLayer />
              <YMapDefaultFeaturesLayer />

              <YMapListener onUpdate={handleMapUpdate} />

              <YMapControls position="right">
                <YMapZoomControl />
              </YMapControls>

              <YMapControls position="right top">
                <YMapGeolocationControl />
              </YMapControls>

              {markers?.length !== 1 && (
              <YMapControls position="left top">
                <YMapControlButton>
                  <span className={styles.mapControlBadge}>
                    {t('map.total')}: {displayedMarkers.length}
                    {selectedRegion && ` / ${normalizedMarkers.length}`}
                  </span>
                </YMapControlButton>
              </YMapControls>
              )}

              <YMapCollection>
                {displayedMarkers.map((marker) => {
                  const statusInfo = STATUS_META[marker.status] || STATUS_META.down;
                  const coordinates = [Number(marker.lon), Number(marker.lat)];
                  const labelStyles = markerLabelLayerStyles(marker.status, marker.label_color);
                  const labelHex = parseValidHex(marker.label_color);

                  return (
                    <YMapMarker
                      key={`${marker.ip || marker.name}-${marker.lat}-${marker.lon}`}
                      coordinates={coordinates}
                    >
                      <div
                        className={`${styles.marker} ${statusInfo.markerClass}${marker.on_fire ? ` ${styles.markerOnFire}` : ''}`}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredMarker(marker)}
                        onMouseLeave={() => setHoveredMarker(null)}
                        onClick={(e) => handleMarkerClick(marker, e)}
                      >
                        {marker.on_fire ? (
                          <span className={styles.markerFire} aria-label="fire" title={t('fire.onFire', { defaultValue: 'В огне' })}>
                            <span className="material-icons" style={{ fontSize: 44, color: '#f97316' }}>
                              local_fire_department
                            </span>
                          </span>
                        ) : (
                          <>
                            <span className={styles.markerGlow} style={labelStyles?.glow} />
                            <span className={styles.markerPulse} style={labelStyles?.pulse} />
                            <span className={styles.markerCore} style={labelStyles?.core} />
                            {labelHex && (
                              <span
                                className={styles.markerLabelBadge}
                                style={{ backgroundColor: labelHex }}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </YMapMarker>
                  );
                })}
              </YMapCollection>
            </YMap>
          </YMapComponentsProvider>
        )}
        
        {/* Тултип при наведении (как в примере) */}
        {hoveredMarker && (
          <div
            className="absolute z-50 bg-surface-raised/95 border border-brand-400/30 rounded-lg shadow-xl backdrop-blur-sm pointer-events-none"
            style={{
              top: mousePos.y - 40,
              left: mousePos.x + 10,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="flex items-center gap-3 px-3 py-2">
              {/* Маленькое фото слева */}
              {hoveredMarker.photo_url ? (
                <div className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-surface-base/50 border border-surface-border/30">
                  <img 
                    src={hoveredMarker.photo_url} 
                    alt={hoveredMarker.name || 'Школа'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.tooltip-photo-placeholder');
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="tooltip-photo-placeholder hidden absolute inset-0 w-full h-full items-center justify-center bg-surface-base/50">
                    <span className="material-icons text-xl text-muted-400">school</span>
                  </div>
                </div>
              ) : (
                <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-surface-base/50 border border-surface-border/30 flex items-center justify-center">
                  <span className="material-icons text-xl text-muted-400">school</span>
                </div>
              )}
              
              {/* Текст справа */}
              <div className="flex flex-col min-w-0">
                <div className="text-sm font-semibold text-white truncate">{hoveredMarker.name || t('map.unknownObject')}</div>
                {hoveredMarker.region && <div className="text-xs text-muted-400 mt-0.5">{hoveredMarker.region}</div>}
                {hoveredMarker.label_name && (
                  <div
                    className="mt-0.5 flex items-center gap-1 truncate text-xs font-medium"
                    style={{ color: hoveredMarker.label_color || '#cbd5e1' }}
                  >
                    <span
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: hoveredMarker.label_color || '#94a3b8' }}
                    />
                    {hoveredMarker.label_name}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
      </div>

      <MapSidebar
        markerData={selectedMarker}
        onClose={handleCloseSidebar}
        isOpen={sidebarOpen}
        onOpenHistory={handleOpenHistory}
        onOpenSpeedtest={handleOpenSpeedtest}
        showActions={!disableDefaultLayout}
        t={t}
        statusMeta={STATUS_META}
      />
      
      <HistoryGraphModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        deviceIP={currentDeviceIp || ''}
        deviceName={selectedMarker?.name || ''}
        timeRange={historyTimeRange}
        onTimeRangeChange={handleHistoryTimeRangeChange}
        chartData={historyChartData}
        isLoading={historyLoading}
      />
      
      <SpeedtestModalWebSocket
        isOpen={speedtestModalOpen}
        onClose={() => setSpeedtestModalOpen(false)}
        deviceName={selectedMarker?.name || currentDeviceIp || ''}
        deviceIP={currentDeviceIp || ''}
      />
    </section>
  );
};

export default MapSection;